/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';

import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import { useLocation, useParams, useRoutes } from 'js/lib/useRouter';

import { breakpoints, useMediaQuery } from '@coursera/cds-core';
import type { EOIButtonSource } from '@coursera/event-pulse-types';

import type { UniversityProgramDeadlines } from 'bundles/degree-description/__generated__/baseContentfulTypes';
import TopBanner from 'bundles/degree-description/components/modules/banner/TopBanner';
import EoiButtonsMobileFooter from 'bundles/degree-description/components/modules/hero/EoiButtonsMobileFooter';
import PersonalizedModal from 'bundles/degree-description/components/modules/personalization/PersonalizedModal';
import PersonalizedTooltip from 'bundles/degree-description/components/modules/personalization/PersonalizedTooltip';
import StickyHeader from 'bundles/degree-description/components/shared/Navigation/StickyHeader';
import { useDegreeDescriptionEoiStateContext } from 'bundles/degree-description/contexts/DegreeDescriptionEoiStateContext';
import { useDegreeDescriptionPageAdmissionDeadlinesData } from 'bundles/degree-description/contexts/DegreeDescriptionPageAdmissionDeadlinesContext';
import type { MergedDegreeDescriptionPageFields } from 'bundles/degree-description/contexts/DegreeDescriptionPageContext';
import { useDegreeDescriptionPageData } from 'bundles/degree-description/contexts/DegreeDescriptionPageContext';
import { useDegreeDescriptionPageElementRefs } from 'bundles/degree-description/contexts/DegreeDescriptionPageElementRefsContext';
import { useDegreeDescriptionUpsellsData } from 'bundles/degree-description/contexts/DegreeDescriptionPageUpsellsContext';
import { useDegreeDescriptionPersonalizedData } from 'bundles/degree-description/contexts/DegreeDescriptionPersonalizationContext';
import {
  DegreeDescriptionEventingContextProvider,
  useDegreeDescriptionEventingContext,
} from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import type { AverageCourseStarRating, PersonalizedData, UpsellsByDegreeSlug } from 'bundles/degree-description/types';
import type { EoiTextType } from 'bundles/degree-description/utils/EoiUtils';
import { deriveEoiButtons, deriveEoiText } from 'bundles/degree-description/utils/EoiUtils';
import { isInLightweightDdp } from 'bundles/degree-description/utils/experiments';
import { getMessagesToDisplay } from 'bundles/degree-description/utils/personalizedMessageCriteria';
import { DDP_TEMPLATE } from 'bundles/degree-description/utils/utils';
import { ChiliPiperProvider } from 'bundles/expression-of-interest/components/ChiliPiper/ChiliPiperProvider';
import EoiDialog from 'bundles/expression-of-interest/components/EoiDialog';
import { EoiOptimizationsContextProvider } from 'bundles/expression-of-interest/components/EoiOptimizationsContext';
import type { RetrieveCompletedSurveyResponse } from 'bundles/expression-of-interest/queries/RetrieveCompletedQualificationSurvey';
import type { EoiButtonsType } from 'bundles/expression-of-interest/types';
import { addDriftChatWidget, setDriftVisibility } from 'bundles/expression-of-interest/utils/driftUtils';
import { getActionQueryParam } from 'bundles/expression-of-interest/utils/util';
import { AUTOCOMPLETE_PHOTO_SIZE, DegreeIcon } from 'bundles/search-common/constants/autocomplete';
import { saveRecentlyViewed } from 'bundles/search-common/utils/SearchUtils';
import { ProductTypes } from 'bundles/university-program-qualification/utils/constants';

const styles = {
  pageContentWrapper: css`
    display: flex;
    flex-direction: column;
    overflow-x: hidden;
    overflow-y: hidden;
    position: relative;
  `,
};

export type PropsFromDegreePageContent = {
  pageContent: MergedDegreeDescriptionPageFields;
  eoiContainerText: EoiTextType;
  eoiButtons: EoiButtonsType;
  setEoiTrackingIndex: React.Dispatch<React.SetStateAction<number>>;
  personalizedDdpData?: PersonalizedData;
  isLightweightDdp?: boolean;
  hasAppFeeWaiverQualification?: boolean;
  averageCourseStarRating?: AverageCourseStarRating;
  eoiSurveyData?: RetrieveCompletedSurveyResponse;
  stackableItemsWithUpsellType?: UpsellsByDegreeSlug;
};

export const DegreePageContent: React.LegacyFunctionComponentWithChildren = ({ children }) => {
  const { id, productVariant, pageContent, hasAppFeeWaiverQualification, averageCourseStarRating, eoiSurveyData } =
    useDegreeDescriptionPageData();

  const {
    setHasCompletedEoi,
    shouldBypassEOIModal,
    isQualified,
    setIsQualified,
    hasCompletedQQForm,
    setHasCompletedQQForm,
    loadingQQUserData,
    highestEducation,
    setHighestEducation,
    learnerTimeline,
    setLearnerTimeline,
    financePlan,
    setFinancePlan,
    eoiButtonsToUse,
    initialSection,
  } = useDegreeDescriptionEoiStateContext();

  const { trackViewDescriptionPage } = useDegreeDescriptionEventingContext();

  const { personalizedDdpData } = useDegreeDescriptionPersonalizedData();
  const { stackableItemsWithUpsellType } = useDegreeDescriptionUpsellsData();

  const { floatingEoiButtonsContainerRef, heroBannerRef } = useDegreeDescriptionPageElementRefs();

  const { slug } = useParams();
  const routes = useRoutes();
  const location = useLocation();

  const [eoiModalIsOpen, setEoiModalIsOpen] = useState(false);
  const [shouldNavigateDirectlyToApplication, setShouldNavigateDirectlyToApplication] = useState(false);
  // This is used to track the source of the EOI interaction/submission
  const [eoiButtonSource, setEoiButtonSource] = useState<EOIButtonSource>('request_info_button');

  const isMobile = useMediaQuery(breakpoints.down('sm'));

  const handleEoiModalClose = () => {
    setEoiModalIsOpen(false);
    setDriftVisibility(true);
  };
  const handleEoiModalOpen = () => {
    setEoiModalIsOpen(true);
    setDriftVisibility(false);
  };

  useEffect(() => {
    // This ensures that the EOI form values are carried forward to the next stage after user is logged in.
    const actionQueryParam = getActionQueryParam(location);
    if (actionQueryParam) {
      handleEoiModalOpen();
    }
    // Intentional since we only want to run this once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    addDriftChatWidget();
  }, []);

  useEffect(() => {
    trackViewDescriptionPage();
    // Only need to update this if the ID updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const currentNavigationRoute = routes[routes.length - 1].name;

  const heroBannerHeight = heroBannerRef?.getBoundingClientRect().height ?? 0;

  useEffect(() => {
    if (window.location.hash) {
      history.replaceState({}, '', location.pathname);
    }
    if (isMobile && currentNavigationRoute !== 'overview') {
      if (window.scrollY > heroBannerHeight) {
        window.scrollTo({
          top: heroBannerHeight + 50,
          behavior: 'smooth',
        });
      }
    } else if (!isMobile && currentNavigationRoute !== 'overview') {
      const offset = -30;
      window.scrollTo({
        top: heroBannerHeight - offset,
        behavior: 'smooth',
      });
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [currentNavigationRoute, isMobile, heroBannerHeight, location.pathname]);

  const handleCtaClick = async () => {
    handleEoiModalOpen();
  };

  const handleOpenEOIModalForApplication = () => {
    setEoiButtonSource('application_button');
    setShouldNavigateDirectlyToApplication(true);
    handleCtaClick();
  };

  // Bypass EOI modal for direct application access when user has engaged with EOI previously
  const modalAwareApplyHandler = shouldBypassEOIModal ? undefined : handleOpenEOIModalForApplication;

  // Expression Of Interest
  const [eoiTrackingIndex, setEoiTrackingIndex] = useState(1);
  const { programAdmissionDeadlinesData } = useDegreeDescriptionPageAdmissionDeadlinesData();
  const eoiModalButtons =
    pageContent &&
    deriveEoiButtons(id, pageContent, handleCtaClick, programAdmissionDeadlinesData, modalAwareApplyHandler);

  const eoiButtons = useMemo(() => {
    return eoiModalButtons?.filter((button) => eoiButtonsToUse.includes(button.actionType));
  }, [eoiButtonsToUse, eoiModalButtons]);

  // Personalization
  const [shouldShowTooltip, setShouldShowTooltip] = useState<boolean>(false);
  const [shouldShowModal, setShouldShowModal] = useState<boolean>(false);

  const messagesToDisplay = getMessagesToDisplay(
    personalizedDdpData,
    slug,
    pageContent?.programName,
    pageContent?.partner?.partnerName,
    pageContent?.deadlineSectionCollection?.items as UniversityProgramDeadlines[]
  );

  useEffect(() => {
    setShouldShowTooltip(!!messagesToDisplay.tooltipMessageToDisplay);
    setShouldShowModal(!!messagesToDisplay.modalMessageToDisplay);
  }, [messagesToDisplay.modalMessageToDisplay, messagesToDisplay.tooltipMessageToDisplay]);

  if (!pageContent || !eoiButtons || !eoiModalButtons) return null;

  const eoiContainerText = deriveEoiText(pageContent, hasAppFeeWaiverQualification);

  const { partner, programName, bannerNotification, template, expressionOfInterest } = pageContent;
  // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const isLightweightDdp = template === DDP_TEMPLATE.lightweightDdp || isInLightweightDdp(slug);

  saveRecentlyViewed({
    id,
    name: programName ?? '',
    slug,
    path: `/degrees/${slug}`,
    imageIconWithSize: partner?.bannerImage?.url
      ? {
          imageUrl: (partner?.bannerImage?.url || partner?.partnerSquareLogo?.url) as string,
          size: AUTOCOMPLETE_PHOTO_SIZE,
        }
      : DegreeIcon,
    partnerName: partner?.partnerName ?? '',
    partnerLogo: partner?.partnerSquareLogo?.url || partner?.bannerImage?.url || DegreeIcon.imageUrl,
    productType: ProductTypes.DEGREE,
  });

  const propsToChildren: PropsFromDegreePageContent = {
    pageContent,
    eoiContainerText,
    eoiButtons,
    setEoiTrackingIndex,
    personalizedDdpData,
    isLightweightDdp,
    hasAppFeeWaiverQualification,
    averageCourseStarRating,
    eoiSurveyData,
    stackableItemsWithUpsellType,
  };

  // Using cloneElement is one of the few ways to pass props to children
  // Doing this here will reduce boilerplate in child components
  const content: React.ReactNode = React.cloneElement(children as React.ReactElement, propsToChildren);

  return (
    <div css={styles.pageContentWrapper} data-testid="degreePage">
      <DegreeDescriptionEventingContextProvider degreeId={id}>
        {/*
         // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess */}
        <ChiliPiperProvider programSlug={slug}>
          <StickyHeader
            partnerLogoUrl={partner?.partnerSquareLogo?.url ?? ''}
            partnerName={partner?.partnerName ?? ''}
            programName={programName ?? ''}
            eoiButtons={eoiButtons}
            eoiContainerText={eoiContainerText}
            eoiHeaderRefElement={floatingEoiButtonsContainerRef}
            setEoiTrackingIndex={setEoiTrackingIndex}
          />
          <TopBanner bannerContent={bannerNotification} />

          {shouldShowTooltip && (
            <PersonalizedTooltip slug={slug} personalizedMessage={messagesToDisplay.tooltipMessageToDisplay} />
          )}

          {content}

          {/*
           // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess */}
          <EoiOptimizationsContextProvider slug={slug} productType={ProductTypes.DEGREE}>
            <EoiDialog
              isOpen={eoiModalIsOpen}
              onClose={handleEoiModalClose}
              onOpen={handleEoiModalOpen}
              productId={tupleToStringKey([ProductTypes.DEGREE, id])}
              applicationUrl={expressionOfInterest?.applyButtonUrl}
              degreeProductVariant={productVariant}
              setHasSubmittedEoi={setHasCompletedEoi}
              // Qualifying Questions experiment
              isQualified={isQualified}
              setIsQualified={setIsQualified}
              hasCompletedQQForm={hasCompletedQQForm}
              setHasCompletedQQForm={setHasCompletedQQForm}
              loadingQQUserData={loadingQQUserData}
              highestEducation={highestEducation}
              setHighestEducation={setHighestEducation}
              learnerTimeline={learnerTimeline}
              setLearnerTimeline={setLearnerTimeline}
              financePlan={financePlan}
              setFinancePlan={setFinancePlan}
              initialSection={initialSection}
              shouldNavigateDirectlyToApplication={shouldNavigateDirectlyToApplication}
              eoiButtonSource={eoiButtonSource}
            />
          </EoiOptimizationsContextProvider>

          <EoiButtonsMobileFooter
            eoiButtons={eoiButtons}
            eoiContainerText={eoiContainerText}
            refElement={floatingEoiButtonsContainerRef}
            currentNavRoute={currentNavigationRoute}
            trackingIndex={eoiTrackingIndex}
            setEoiTrackingIndex={setEoiTrackingIndex}
          />
          {shouldShowModal && (
            <PersonalizedModal
              slug={slug}
              personalizedMessage={messagesToDisplay.modalMessageToDisplay}
              eoiButtons={eoiButtons}
            />
          )}
        </ChiliPiperProvider>
      </DegreeDescriptionEventingContextProvider>
    </div>
  );
};
